import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GET_SHARE_LIST, GET_SHARE_LIST_FILTERS } from "./queryKey";
import { getDbtDocsShareList, DbtDocsShare, DbtDocsShareListResponse } from "./types";
import { RelativeComponentLoader } from "../../components/loader";
import { Card, CardContent } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { FilterTagWrapper } from "../../components/tags";
import { DataTable } from "../../components/tables";
import { Paginate } from "../../components/paginate";
import { DEFAULT_PAGE_SIZE } from "../../components/paginate/constants";
import { DbtDocsEmptySharesList } from "./components/DbtDocsEmptySharesList";
import { useDbtDocsTableHeaders } from "./useDbtDocsTableHeaders";
import { getDbtDocsShareListFilters, getDbtDocsShareUrl, deleteDbtDocsShare } from "@/helpers/apis";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { parseSortDirection } from "@/components/tables/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";

function DbtDocsSharesList() {
  useBreadcrumb("dbt Docs");

  const { searchParams } = useFiltersBySearchParams();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const sortAttribute = searchParams.get("sortAttribute") || "created_at";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const [nameFilter, setNameFilter] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string[]>([]);
  const [shareAppURLs, setShareAppURLs] = useState<Record<string, string>>({});
  const [ownerFilterIdToName, setOwnerFilterIdToName] = useState<Record<string, string>>({});
  const [ownerFilterNameToId, setOwnerFilterNameToId] = useState<Record<string, string>>({});

  const { data: filters = { names: [], owners: [] }, isLoading: isfiltersLoading } = useQuery({
    queryKey: [GET_SHARE_LIST_FILTERS],
    queryFn: () => getDbtDocsShareListFilters(),
  });

  // Handle filters data (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (filters) {
      filters.owners.forEach(owner => {
        const ownerId = String(owner.id);
        setOwnerFilterIdToName(prev => ({ ...prev, [ownerId]: owner.email }));
        setOwnerFilterNameToId(prev => ({ ...prev, [owner.email]: ownerId }));
      });
    }
  }, [filters]);

  const getFilteredParams = () => {
    const params: Record<string, unknown> = {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: sortOrder || "desc",
    };
    return params;
  };

  const { data: dbtShares, isLoading } = useQuery<DbtDocsShareListResponse | undefined>({
    queryKey: [
      GET_SHARE_LIST,
      sortAttribute,
      sortOrder,
      page,
      pageSize,
      ...nameFilter,
      ...ownerFilter,
    ],
    queryFn: () => {
      const params = getFilteredParams();
      if (nameFilter.length > 0) params.names = nameFilter;
      if (ownerFilter.length > 0) params.owners = ownerFilter;
      return getDbtDocsShareList(params);
    },
    staleTime: 0,
  });

  // Handle dbtShares data (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    const fetchShareUrls = async () => {
      if (!dbtShares?.items) return;
      const shareUrls = await Promise.all(
        dbtShares.items.map(async share => {
          try {
            const urlData = await getDbtDocsShareUrl(share.share_id);
            return { [share.share_id]: urlData?.app_url ?? "" };
          } catch {
            return { [share.share_id]: "" };
          }
        })
      );
      setShareAppURLs(Object.assign({}, ...shareUrls));
    };
    fetchShareUrls();
  }, [dbtShares]);

  const queryClient = useQueryClient();
  const { mutate: deleteDbtShare } = useMutation({
    mutationFn: (shareId: string) => deleteDbtDocsShare(shareId),
    onSuccess: data => {
      if (data?.ok) queryClient.invalidateQueries({ queryKey: [GET_SHARE_LIST] });
    },
  });

  const header = useDbtDocsTableHeaders({
    filters,
    nameFilter,
    setNameFilter,
    ownerFilter,
    setOwnerFilter,
    shareAppURLs,
    onDelete: (shareId: string) => deleteDbtShare(shareId),
  });

  if (isLoading || isfiltersLoading)
    return <RelativeComponentLoader label="Loading dbt docs shares" />;
  if (!dbtShares) return null;
  if (dbtShares.total === 0 && nameFilter.length === 0 && ownerFilter.length === 0) {
    return <DbtDocsEmptySharesList />;
  }

  const handleRemoveNameFilter = (removedFilter?: string | string[]) => {
    if (typeof removedFilter === "string") {
      setNameFilter(nameFilter.filter(filter => filter !== removedFilter));
    }
  };

  const handleRemoveOwnerFilter = (removedFilter?: string | string[]) => {
    if (typeof removedFilter === "string") {
      setOwnerFilter(ownerFilter.filter(filter => filter !== ownerFilterNameToId[removedFilter]));
    }
  };

  return (
    <Card>
      <CardContent className="al-pt-4">
        <Stack direction="column" className="al-p-1 al-bg-white">
          <Stack wrap gap="xs" className="al-mb-2">
            <FilterTagWrapper
              applicableFor={undefined}
              filters={{
                nameFilter: {
                  filterStr: nameFilter,
                  label: "Name",
                  onclose: handleRemoveNameFilter,
                },
                userFilter: {
                  filterStr: ownerFilter.map(id => ownerFilterIdToName[id]),
                  label: "User",
                  onclose: handleRemoveOwnerFilter,
                },
              }}
            />
          </Stack>
          <DataTable
            header={header}
            data={{ items: dbtShares.items }}
            onRowClick={() => {}}
            idKey={(item: DbtDocsShare) => item.share_id}
            resetPage={() => setPage(0)}
          />
          <Paginate
            itemCount={dbtShares.total}
            page={page}
            pageSize={pageSize}
            numPages={dbtShares.pages}
            onPageClick={setPage}
            onPageSizeChange={(newSize: number) => {
              setPageSize(newSize);
              setPage(0);
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default DbtDocsSharesList;
